// Polish #1 — "Compose new ticket from scratch."
//
// Modal that lets Kimi open a brand-new Gmail thread without hunting for the
// customer's existing conversation (or when there isn't one yet). Triggered
// by the full-width "+ Start a new conversation" bar on Standup or the `c`
// keyboard shortcut.
//
// Structure (top → bottom):
//   Send-from picker (MD Gmail / HK Gmail) — brand selects voice + signature
//   To (email)
//   Subject
//   Body textarea
//   Haiku draft helper — optional collapsible row with a one-line prompt
//     that fills the body. If Kimi already has text in body, the helper
//     replaces it (with a short confirmation via a plain-text "Replace?"
//     button state) so she doesn't lose accidental keystrokes.
//   Cancel / Send buttons
//
// On success we close the modal and call `onSent(conversationId)` so the
// parent (Standup) can navigate into the new thread's Command Center.

import { useEffect, useRef, useState } from 'react';
import {
  useComposeNewTicket,
  useDraftFromPrompt,
  type ComposeBrand,
} from '@/lib/hooks/useComposeNewTicket';

export interface NewTicketModalProps {
  open: boolean;
  onClose: () => void;
  /**
   * Fired after a successful send. Caller is expected to navigate the user
   * into the new thread (usually via react-router `navigate(/thread/:id)`).
   */
  onSent: (conversationId: string) => void;
  /** Optional default brand. Defaults to 'md'. */
  defaultBrand?: ComposeBrand;
}

// Rudimentary email format check matches the backend's EMAIL_RE. We don't
// block on this — the send button stays enabled — but we use it to gate
// the "looks valid" affordance + gently disable send if obviously broken.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function NewTicketModal({
  open,
  onClose,
  onSent,
  defaultBrand = 'md',
}: NewTicketModalProps) {
  const compose = useComposeNewTicket();
  const draft = useDraftFromPrompt();

  const [brand, setBrand] = useState<ComposeBrand>(defaultBrand);
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  // Haiku helper state. `promptOpen` toggles the collapsible row.
  const [promptOpen, setPromptOpen] = useState(false);
  const [prompt, setPrompt] = useState('');

  // Error state shown inline above the Send button. Separate from the
  // draft-helper error which surfaces in its own row.
  const [sendError, setSendError] = useState<string | null>(null);
  const [draftError, setDraftError] = useState<string | null>(null);

  const toInputRef = useRef<HTMLInputElement | null>(null);
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);

  // Reset whenever the modal opens — prevents last-send leakage if Kimi
  // opens the composer twice in a row. Keep the brand, because that's
  // usually consistent across a batch of ad-hoc sends.
  useEffect(() => {
    if (open) {
      setTo('');
      setSubject('');
      setBody('');
      setPromptOpen(false);
      setPrompt('');
      setSendError(null);
      setDraftError(null);
      // Focus To after the modal paints.
      const raf = requestAnimationFrame(() => toInputRef.current?.focus());
      return () => cancelAnimationFrame(raf);
    }
    return;
  }, [open]);

  // Esc closes; mirrors ConfirmDialog's UX.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        if (!compose.isPending && !draft.isPending) onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, compose.isPending, draft.isPending]);

  if (!open) return null;

  const toValid = EMAIL_RE.test(to.trim());
  const canSend =
    toValid &&
    subject.trim().length > 0 &&
    body.trim().length > 0 &&
    !compose.isPending;

  const handleGenerate = async () => {
    setDraftError(null);
    const cleanPrompt = prompt.trim();
    if (!cleanPrompt) {
      setDraftError('Describe what you want to say in a line or two.');
      return;
    }
    // Try to pluck a first-name guess from the To input. Anything before
    // the @, stopped at the first dot/underscore/plus. Fallback: nothing —
    // the prompt will just open warmly without a name.
    const localPart = to.trim().split('@')[0] ?? '';
    const rawName = localPart.split(/[._+]/)[0] ?? '';
    const toName =
      rawName.length >= 2
        ? rawName.charAt(0).toUpperCase() + rawName.slice(1).toLowerCase()
        : undefined;

    try {
      const result = await draft.mutateAsync({ brand, prompt: cleanPrompt, to_name: toName });
      setBody(result.text);
      // Close the prompt row so the body textarea is front-and-center
      // for Kimi to edit.
      setPromptOpen(false);
      // Move caret to end so subsequent typing appends.
      requestAnimationFrame(() => {
        const el = bodyRef.current;
        if (el) {
          el.focus();
          el.setSelectionRange(el.value.length, el.value.length);
        }
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Haiku did not reply.';
      setDraftError(msg);
    }
  };

  const handleSend = async () => {
    setSendError(null);
    if (!canSend) return;
    try {
      const result = await compose.mutateAsync({
        brand,
        to: to.trim(),
        subject: subject.trim(),
        body: body.trim(),
      });
      onSent(result.conversation_id);
      onClose();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Send failed.';
      setSendError(msg);
    }
  };

  return (
    <div
      className="confirm-dialog-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="new-ticket-title"
      onClick={
        compose.isPending || draft.isPending ? undefined : onClose
      }
    >
      <div
        className="new-ticket-card"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="new-ticket-head">
          <div className="eyebrow" style={{ marginBottom: 4 }}>New conversation</div>
          <h2 id="new-ticket-title" className="new-ticket-title">
            Start fresh with someone.
          </h2>
        </div>

        <div className="new-ticket-body">
          {/* Send-from picker */}
          <label className="new-ticket-field">
            <span className="new-ticket-label">Send from</span>
            <div className="new-ticket-brand-picker">
              <button
                type="button"
                className={`new-ticket-brand ${brand === 'md' ? 'is-active' : ''}`}
                onClick={() => setBrand('md')}
                disabled={compose.isPending}
              >
                <span className="new-ticket-brand-name">Market Dwellings</span>
                <span className="new-ticket-brand-email">hello@marketdwellings.com</span>
              </button>
              <button
                type="button"
                className={`new-ticket-brand ${brand === 'hk' ? 'is-active' : ''}`}
                onClick={() => setBrand('hk')}
                disabled={compose.isPending}
              >
                <span className="new-ticket-brand-name">HarperKellee</span>
                <span className="new-ticket-brand-email">hello@harperkellee.com</span>
              </button>
            </div>
          </label>

          {/* To */}
          <label className="new-ticket-field">
            <span className="new-ticket-label">To</span>
            <input
              ref={toInputRef}
              type="email"
              className="new-ticket-input"
              placeholder="jane@example.com"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              disabled={compose.isPending}
            />
          </label>

          {/* Subject */}
          <label className="new-ticket-field">
            <span className="new-ticket-label">Subject</span>
            <input
              type="text"
              className="new-ticket-input"
              placeholder="What's this about?"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              disabled={compose.isPending}
            />
          </label>

          {/* Body */}
          <label className="new-ticket-field">
            <span className="new-ticket-label">
              Message
              <button
                type="button"
                className="new-ticket-prompt-toggle"
                onClick={() => setPromptOpen((v) => !v)}
                disabled={compose.isPending || draft.isPending}
              >
                {promptOpen ? 'Close draft helper' : 'Draft with Haiku'}
              </button>
            </span>
            <textarea
              ref={bodyRef}
              className="new-ticket-textarea"
              placeholder="Write the email. The signature gets appended on send."
              rows={8}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              disabled={compose.isPending}
            />
          </label>

          {/* Haiku prompt row */}
          {promptOpen ? (
            <div className="new-ticket-prompt-row">
              <input
                type="text"
                className="new-ticket-input"
                placeholder="One line: what should Kimi say?"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !draft.isPending) {
                    e.preventDefault();
                    void handleGenerate();
                  }
                }}
                disabled={draft.isPending || compose.isPending}
                autoFocus
              />
              <button
                type="button"
                className="btn primary sm"
                onClick={handleGenerate}
                disabled={draft.isPending || compose.isPending}
              >
                {draft.isPending ? 'Drafting…' : 'Generate'}
              </button>
            </div>
          ) : null}
          {draftError ? (
            <div className="new-ticket-inline-error">{draftError}</div>
          ) : null}
        </div>

        {sendError ? (
          <div className="new-ticket-inline-error" style={{ marginTop: 6 }}>
            {sendError}
          </div>
        ) : null}

        <div className="confirm-dialog-actions" style={{ marginTop: 18 }}>
          <button
            type="button"
            className="btn"
            onClick={onClose}
            disabled={compose.isPending}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn primary"
            onClick={handleSend}
            disabled={!canSend}
          >
            {compose.isPending ? 'Sending…' : `Send as ${brand === 'hk' ? 'HarperKellee' : 'Market Dwellings'}`}
          </button>
        </div>
      </div>
    </div>
  );
}
